/**
 * Password Change Banner - Web Version
 *
 * Dismissible banner prompting B2B customers to change their initial password.
 * Appears below the header on the FleetHub dashboard.
 *
 * @module components/PasswordChangeBanner
 * @platform web
 */

import { FC, useState } from "react"
import { View, ViewStyle, TextStyle, Pressable } from "react-native"
import { KeyRound, X } from "lucide-react"

import { Text } from "@/components/Text"
import { colors } from "@/theme"

interface PasswordChangeBannerProps {
  onChangePassword: () => void
  onDismiss: () => void
  style?: ViewStyle
}

export const PasswordChangeBanner: FC<PasswordChangeBannerProps> = ({
  onChangePassword,
  onDismiss,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <View style={[$banner, style]}>
      <View style={$content}>
        <KeyRound size={18} color={colors.palette.neutral800} strokeWidth={2} />
        <Text style={$message}>
          Bitte ändere dein initial von uns vergebenes Passwort, um deine Accounteinrichtung
          abzuschließen.
        </Text>
        <Pressable
          onPress={onChangePassword}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={[$button, isHovered && $buttonHover]}
          accessibilityRole="button"
          accessibilityLabel="Passwort ändern"
        >
          <Text style={$buttonText}>Passwort ändern</Text>
        </Pressable>
      </View>
      <Pressable
        onPress={onDismiss}
        style={$closeButton}
        accessibilityRole="button"
        accessibilityLabel="Hinweis schließen"
      >
        <X size={16} color={colors.textDim} strokeWidth={2} />
      </Pressable>
    </View>
  )
}

const $banner: ViewStyle = {
  position: "absolute",
  left: 0,
  right: 0,
  zIndex: 999,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingHorizontal: 24,
  paddingVertical: 10,
  backgroundColor: "rgba(255, 249, 235, 0.95)",
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  boxShadow: "0 1px 0 rgba(17, 30, 44, 0.06)",
}

const $content: ViewStyle = {
  flex: 1,
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
}

const $message: TextStyle = {
  fontSize: 14,
  color: colors.palette.neutral800,
  fontWeight: "500",
  flex: 1,
  minWidth: 200,
}

const $button: ViewStyle = {
  paddingHorizontal: 16,
  paddingVertical: 8,
  backgroundColor: colors.palette.neutral800,
  borderRadius: 8,
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "all 150ms ease",
}

const $buttonHover: ViewStyle = {
  backgroundColor: colors.surfaceDarkHover,
}

const $buttonText: TextStyle = {
  fontSize: 13,
  fontWeight: "600",
  color: colors.palette.neutral100,
}

const $closeButton: ViewStyle = {
  padding: 8,
  marginLeft: 8,
  borderRadius: 6,
  // @ts-ignore
  cursor: "pointer",
}
